/**
 * PlusReportCompactStrip — a compact ~80px-tall variant of
 * PlusReportBuyCard. Designed to be dropped into high-intent surfaces
 * (Shortlist, Advanced Results, Compare) where a user has just
 * completed the work the $49 report is meant to make sense of.
 *
 * Shares the same Stripe hosted-checkout flow as PlusReportBuyCard —
 * signed-out users get a sign-in prompt; signed-in users go straight
 * to Stripe. The `context` prop lets each host page provide a
 * one-line hook that reads naturally at that moment.
 *
 * Added 2026-02-05 as part of the "high-intent placements" push.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const DEFAULT_HOOK =
  "You've done the hard work. A WUA Personalised Report turns it into a plan.";

export default function PlusReportCompactStrip({
  hook = DEFAULT_HOOK,
  testId = "plus-report-compact-strip",
}) {
  const { user, login } = useAuth();
  const [busy, setBusy] = useState(false);

  const startCheckout = async () => {
    if (!user) { login(); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/wua-plus/checkout", {
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
        "Could not start checkout — please try again in a moment."
      );
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={testId}
      className="relative rounded-lg border border-[#4C1D95]/30 bg-gradient-to-r from-[#F5F0FF] via-white to-[#F5F0FF] p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 shadow-[0_1px_0_rgba(76,29,149,0.08)]"
    >
      <div className="flex items-start gap-3 min-w-0">
        <div className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#4C1D95] text-white">
          <Sparkles className="h-4 w-4" aria-hidden />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-underground-sc tracking-wider text-emerald-800">
              Pre-WUA+
            </span>
            <span className="font-editorial text-lg text-[#002147] leading-tight">
              WUA Personalised Report — $49 one-off
            </span>
          </div>
          <p className="mt-1 text-[13px] text-stone-600 leading-snug">
            {hook}{" "}
            <Link
              to="/wua-plus/sample"
              className="text-[#4C1D95] hover:underline"
              data-testid={`${testId}-see-sample`}
            >
              See a sample
            </Link>
            .
          </p>
        </div>
      </div>
      <Button
        onClick={startCheckout}
        disabled={busy}
        data-testid={`${testId}-buy-cta`}
        className="shrink-0 bg-[#4C1D95] hover:bg-[#5B21B6] text-white font-medium rounded-md"
      >
        {busy ? (
          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting…</>
        ) : (
          <>Get my $49 report <ArrowRight className="h-4 w-4 ml-2" /></>
        )}
      </Button>
    </div>
  );
}
